import { ref, computed, onUnmounted, nextTick } from 'vue';
import { useRoute, useRouter, type LocationQueryValue } from 'vue-router';
import { isAxiosError } from 'axios';
import type { DocumentRevision } from '@/entities/Document';
import type { SortOrder } from '@/shared/lib';
import type { FilterOptions } from '@/entities/Document';
import { FilteredListStatus } from '@/entities/Document';
import { formatDateForApi, parseDate, useCustomToast } from '@/shared/lib';
import { documentApi, type DirectoryListingParams } from '@/entities/Document';
import { getModuleContent } from '../api/module-content.api';
import { isDirectoryContainerCode, isModuleCode } from './mapVisibleTreeToNavigation';
import { containerNotFoundToastTitle } from './directoryContainerNotFound';
import { useVisibleDirectoryTree } from './useVisibleDirectoryTree';
import { hasActiveFilterValues } from './filterActive.helpers';

export interface DocumentFilterParams {
  folderId?: string;
  search?: string;
  status?: string[];
  dateRange?: {
    start: string;
    end: string;
  };
  page?: number;
  limit?: number;
  orderBy?: string;
  orderDirection?: SortOrder;
}

export interface DocumentFilterResponse {
  documents: DocumentRevision[];
  metadata: {
    page: number;
    total: number;
    itemsPerPage: number;
    totalPages: number;
  };
}

const DEFAULT_METADATA = {
  page: 1,
  total: 0,
  itemsPerPage: 10,
  totalPages: 0,
};

function mapOrderByToApi(orderBy: string): string {
  const orderMap: Record<string, string> = {
    effectiveDate: 'effectiveDate',
    name: 'name',
    revision: 'revisionCount',
    revisionCount: 'revisionCount',
    prefix: 'prefixValue',
    documentId: 'prefixValue',
    prefixValue: 'prefixValue',
    createdAt: 'createdAt',
  };

  return orderMap[orderBy] ?? 'effectiveDate';
}

function getSingleQueryValue(
  value: LocationQueryValue | LocationQueryValue[] | undefined
): LocationQueryValue | undefined {
  return Array.isArray(value) ? (value[0] ?? null) : value;
}

export function useFilteredList() {
  const route = useRoute();
  const router = useRouter();
  const { showErrorToast } = useCustomToast();
  const searchType = ref<'name' | 'prefix'>('prefix');
  const setSearchType = (type: 'name' | 'prefix') => {
    searchType.value = type;
  };

  const state = ref({
    loading: false,
    // Only a module or folder listing turns a failed load into an error with a retry; the other
    // listings keep their previous empty fallback.
    loadError: false,
    documents: [] as DocumentRevision[],
    metadata: { ...DEFAULT_METADATA },
  });

  const searchTerm = ref('');
  const selectedOrder = ref('');
  const selectedOrderDirection = ref<SortOrder>('asc');
  const selectedQuantity = ref(10);
  const currentPage = ref(1);
  const currentFilter = ref<FilterOptions>({});
  const currentId = ref<string>('');
  const hasHydratedFromUrl = ref(false);
  let fetchGeneration = 0;

  function applyRevisionParam(filter: FilterOptions, value: LocationQueryValue | undefined) {
    if (value === undefined || value === null || String(value).trim() === '') {
      return;
    }
    const rev = Number(value);
    if (Number.isFinite(rev) && rev > 0) {
      filter.revision = rev;
    }
  }

  function applyDateParam(
    filter: FilterOptions,
    key: 'effectiveDate' | 'createdAt' | 'completedAt',
    value: LocationQueryValue | undefined
  ) {
    if (value && typeof value === 'string') {
      const d = parseDate(value);
      if (d) filter[key] = d;
    }
  }

  function buildFilterFromQuery(q: typeof route.query): FilterOptions {
    const filter: FilterOptions = {};
    if (q.status && typeof q.status === 'string') filter.status = q.status as FilteredListStatus;
    if (q.type && typeof q.type === 'string') filter.type = q.type;
    applyRevisionParam(filter, getSingleQueryValue(q.revision));
    if (q.hasTraining === 'true' || q.hasTraining === 'false') {
      filter.hasTraining = q.hasTraining === 'true';
    }
    applyDateParam(filter, 'effectiveDate', getSingleQueryValue(q.effectiveDate));
    if (q.stage && typeof q.stage === 'string') filter.stage = q.stage;
    applyDateParam(filter, 'createdAt', getSingleQueryValue(q.createdAt));
    applyDateParam(filter, 'completedAt', getSingleQueryValue(q.completedAt));
    return filter;
  }

  function hydrateFromQuery() {
    const q = route.query;
    searchTerm.value = (q.term as string) ?? '';
    selectedOrder.value = (q.sort as string) ?? '';
    selectedOrderDirection.value = (q.order === 'desc' ? 'desc' : 'asc') as SortOrder;
    currentPage.value = Math.max(1, Number(q.page) || 1);
    const amount = Number(q.amount) || 10;
    if ([10, 25, 50, 100].includes(amount)) selectedQuantity.value = amount;
    const st = q.searchType as string;
    if (st === 'prefix' || st === 'name') {
      setSearchType(st);
    }
    currentFilter.value = buildFilterFromQuery(q);
    hasHydratedFromUrl.value = true;
  }

  function buildQueryFromState(): Record<string, string> {
    const query: Record<string, string> = {};
    if (searchTerm.value) {
      query.term = searchTerm.value;
      query.searchType = searchType.value;
    }
    if (selectedOrder.value) {
      query.sort = selectedOrder.value;
      query.order = selectedOrderDirection.value;
    }
    if (currentPage.value > 1) query.page = String(currentPage.value);
    if (selectedQuantity.value !== 10) query.amount = String(selectedQuantity.value);
    Object.entries(currentFilter.value).forEach(([key, value]) => {
      if (value === null || value === undefined || value === '') {
        return;
      }
      if (key === 'revision' && typeof value === 'number' && value <= 0) {
        return;
      }
      if (value instanceof Date) {
        const s = formatDateForApi(value);
        if (s) query[key] = s;
        return;
      }
      if (typeof value === 'boolean') {
        query[key] = String(value);
        return;
      }
      query[key] = String(value);
    });
    return query;
  }

  function syncUrl() {
    if (!hasHydratedFromUrl.value) return;
    router.replace({ path: route.path, query: buildQueryFromState() });
  }

  hydrateFromQuery();

  const totalPages = computed(
    () =>
      state.value.metadata.totalPages ||
      Math.ceil(state.value.metadata.total / selectedQuantity.value)
  );
  const totalItems = computed(() => state.value.metadata.total);
  const itemsPerPage = computed(() => selectedQuantity.value);

  const hasResults = computed(() => state.value.documents && state.value.documents.length > 0);
  const hasActiveFilters = computed(() => hasActiveFilterValues(currentFilter.value));

  const shouldShowFilterNoResults = computed(
    () =>
      !hasResults.value &&
      !state.value.loading &&
      (!!searchTerm.value.trim() || hasActiveFilters.value)
  );

  const shouldShowNoResults = computed(
    () => !hasResults.value && !!searchTerm.value.trim() && !state.value.loading
  );

  const shouldShowEmptyState = computed(
    () =>
      !hasResults.value &&
      !state.value.loading &&
      !searchTerm.value.trim() &&
      !hasActiveFilters.value
  );

  const { load: loadVisibleTree, findFolderByCode, findModuleByCode } = useVisibleDirectoryTree();

  const resetDocumentState = () => {
    state.value.documents = [];
    state.value.metadata = { ...DEFAULT_METADATA };
  };

  const applyDocumentResponse = (
    documents: DocumentRevision[],
    metadata?: DocumentFilterResponse['metadata']
  ) => {
    state.value.documents = documents;
    state.value.metadata.page = metadata?.page ?? currentPage.value;
    state.value.metadata.total = metadata?.total ?? 0;
    state.value.metadata.itemsPerPage = metadata?.itemsPerPage ?? selectedQuantity.value;
    state.value.metadata.totalPages = metadata?.totalPages ?? 0;
  };

  const loadFolderMapping = async (): Promise<boolean> => {
    try {
      await loadVisibleTree();
      return true;
    } catch {
      return false;
    }
  };

  const extractFolderIdFromPath = async (id: string): Promise<number | null> => {
    if (!id) return null;

    const loaded = await loadFolderMapping();
    if (!loaded) return null;

    return findFolderByCode(id)?.folder.id ?? null;
  };

  const buildFolderParams = (): DirectoryListingParams => {
    const term = searchTerm.value.trim();
    const filter = currentFilter.value;
    const folderParams: DirectoryListingParams = {
      page: currentPage.value,
      limit: selectedQuantity.value,
      sort: mapOrderByToApi(selectedOrder.value || 'effectiveDate'),
      order: selectedOrderDirection.value,
      term: term || undefined,
      searchType: term ? searchType.value : undefined,
      status: filter.status || undefined,
    };

    if (filter.revision !== undefined && filter.revision !== null && Number(filter.revision) > 0) {
      folderParams.revision = String(filter.revision);
    }

    if (filter.hasTraining === true || filter.hasTraining === false) {
      folderParams.hasTraining = String(filter.hasTraining);
    }

    if (filter.effectiveDate instanceof Date) {
      const effectiveDate = formatDateForApi(filter.effectiveDate);
      if (effectiveDate) {
        folderParams.effectiveDate = effectiveDate;
      }
    }

    return folderParams;
  };

  const requestContainerDocuments = async (id: string) => {
    if (!isDirectoryContainerCode(id)) {
      const folderId = await extractFolderIdFromPath(id);
      return folderId ? documentApi.getDocumentsByFolder(folderId, buildFolderParams()) : null;
    }

    // A tree that cannot be loaded is a failed load, not a missing container: it throws here so the
    // page offers a retry instead of sending the member back to the Home.
    await loadVisibleTree();

    if (isModuleCode(id)) {
      const moduleId = findModuleByCode(id)?.moduleId;
      return moduleId ? getModuleContent(moduleId, buildFolderParams()) : null;
    }

    const folderId = findFolderByCode(id)?.folder.id;
    return folderId ? documentApi.getDocumentsByFolder(folderId, buildFolderParams()) : null;
  };

  const leaveMissingContainer = async (id: string): Promise<void> => {
    resetDocumentState();
    if (isDirectoryContainerCode(id)) {
      showErrorToast({ title: containerNotFoundToastTitle(id), type: 'error' });
    }
    await router.replace({ name: 'Home' });
  };

  const fetchFolderDocuments = async (id: string, generation: number): Promise<void> => {
    const response = await requestContainerDocuments(id);

    if (generation !== fetchGeneration) {
      return;
    }

    if (!response) {
      await leaveMissingContainer(id);
      return;
    }

    applyDocumentResponse(response.data ?? [], response.metadata);
  };

  const fetchDocuments = async (type: string, id: string) => {
    // Nothing cancels an in-flight folder request: without this, a slow response for a listing the
    // member already left lands on the one now on screen.
    const generation = ++fetchGeneration;

    if (id) {
      currentId.value = id;
    }
    state.value.loading = true;
    state.value.loadError = false;

    try {
      await fetchFolderDocuments(id, generation);
    } catch (error) {
      if (generation !== fetchGeneration) return;

      if (isAxiosError(error) && error.response?.status === 404) {
        await leaveMissingContainer(id);
        return;
      }

      resetDocumentState();
      state.value.loadError = isDirectoryContainerCode(id);
    } finally {
      if (generation === fetchGeneration) {
        state.value.loading = false;
      }
    }
  };

  const retryFetch = async () => {
    await fetchDocuments('', currentId.value);
  };

  const handleSearch = async (term: string) => {
    searchTerm.value = term;
    currentPage.value = 1;
    syncUrl();
    await fetchDocuments('', currentId.value);
  };

  const handleSort = async (sort: string, order: string = 'asc') => {
    selectedOrder.value = sort;
    selectedOrderDirection.value = (order || 'asc') as SortOrder;
    currentPage.value = 1;
    syncUrl();
    await fetchDocuments('', currentId.value);
  };

  const handleFilter = async (filters: FilterOptions) => {
    currentFilter.value =
      filters && Object.keys(filters as object).length > 0
        ? { ...currentFilter.value, ...filters }
        : {};
    currentPage.value = 1;
    syncUrl();
    await fetchDocuments('', currentId.value);
  };

  const scrollToPagination = async () => {
    await nextTick();
    const paginationNav = document.querySelector<HTMLElement>(
      'nav[role="navigation"][aria-label*="páginas"]'
    );
    if (paginationNav) {
      paginationNav.scrollIntoView({ behavior: 'smooth', block: 'center' });
      paginationNav.querySelector<HTMLButtonElement>('button[aria-current="page"]')?.focus();
    }
  };

  const handlePageChange = async (page: number) => {
    currentPage.value = page;
    syncUrl();
    await fetchDocuments('', currentId.value);
    await scrollToPagination();
  };

  const handleQuantityChange = async (quantity: number) => {
    selectedQuantity.value = quantity;
    currentPage.value = 1;
    syncUrl();
    await fetchDocuments('', currentId.value);
  };

  const handleExport = () => {
    // TODO: Implementar exportação
  };

  const clearCache = () => {
    currentId.value = '';
  };

  const clearSearch = () => {
    searchTerm.value = '';
    selectedOrder.value = '';
    selectedOrderDirection.value = 'asc';
    currentPage.value = 1;
    currentFilter.value = {};
    resetDocumentState();
    clearCache();
  };

  onUnmounted(() => {
    clearCache();
  });

  return {
    state,
    searchTerm,
    searchType,
    setSearchType,
    selectedOrder,
    selectedOrderDirection,
    selectedQuantity,
    currentPage,
    totalPages,
    totalItems,
    itemsPerPage,
    currentFilter,
    hasResults,
    hasActiveFilters,
    shouldShowNoResults,
    shouldShowFilterNoResults,
    shouldShowEmptyState,
    handleSearch,
    handleSort,
    handleFilter,
    handlePageChange,
    handleQuantityChange,
    handleExport,
    fetchDocuments,
    retryFetch,
    clearCache,
    clearSearch,
    hydrateFromQuery,
    hasHydratedFromUrl,
  };
}
